import React, { useState, useEffect, useRef } from 'react';
import {
    Smartphone,
    ChevronLeft,
    AlertCircle,
    ShieldCheck,
    CheckCircle2,
    RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { useKYC } from '@/shared/hooks/useKYC';

const PhoneVerificationPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { status, submitApplication } = useKYC(user?.uid);

    // UI State
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const VERSION = "1.2.0_MANUAL_VERIFICATION";

    // 1. Submit Verification Request
    const handleSubmitRequest = async () => {
        if (!phoneNumber) return;

        // Simple validation
        const cleaned = phoneNumber.replace(/-/g, '');
        if (!/^010\d{8}$/.test(cleaned) && !/^\+8210\d{8}$/.test(cleaned)) {
            alert('올바른 휴대폰 번호 형색이 아닙니다 (예: 010-1234-5678)');
            return;
        }

        setIsSubmitting(true);
        console.log(`[${VERSION}] Submitting Phone Verification Request:`, phoneNumber);

        try {
            const result = await submitApplication('PHONE', {
                phoneNumber,
                method: 'MANUAL_APPROVAL',
                requestedAt: new Date().toISOString()
            });

            if (result.success) {
                alert('인증 요청이 접수되었습니다. 관리자 승인 후 완료됩니다.');
            } else {
                alert('요청 제출 실패: ' + (result.error as any)?.message);
            }
        } catch (error: any) {
            console.error("Submission Error:", error);
            alert('오류가 발생했습니다: ' + error.message);
        }
        setIsSubmitting(false);
    };

    if (status?.phone === 'Pending') {
        return (
            <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-700 text-center py-20">
                <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-8">
                    <RefreshCw className="h-10 w-10 text-amber-500 animate-spin" />
                </div>
                <h1 className="text-3xl font-black text-white">인증 승인 대기 중</h1>
                <p className="text-slate-500 font-bold">
                    관리자가 휴대폰 번호({phoneNumber || '제출됨'})를 확인 중입니다.<br />
                    승인이 완료되면 자동으로 인증 상태가 변경됩니다.
                </p>
                <button
                    onClick={() => navigate('/verification')}
                    className="mt-8 px-8 py-3 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-700 transition-all"
                >
                    인증 센터로 돌아가기
                </button>
            </div>
        );
    }

    if (status?.phone === 'Completed') {
        return (
            <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-700 text-center py-20">
                <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-8">
                    <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                </div>
                <h1 className="text-3xl font-black text-white">인증 완료</h1>
                <p className="text-slate-500 font-bold">휴대폰 본인 확인이 성공적으로 완료되었습니다.</p>
                <button
                    onClick={() => navigate('/verification')}
                    className="mt-8 px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-500 transition-all"
                >
                    확인
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-700">
            <button
                onClick={() => navigate('/verification')}
                className="flex items-center gap-2 text-slate-500 hover:text-white transition-colors text-sm font-bold group"
            >
                <ChevronLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" /> 뒤로가기
            </button>

            <div className="space-y-2">
                <h1 className="text-3xl font-black text-white flex items-center gap-3">
                    <Smartphone className="h-8 w-8 text-indigo-500" /> 휴대폰 번호 제출
                </h1>
                <p className="text-slate-500 font-bold text-sm">본인 명의의 휴대폰 번호를 입력 후 승인을 요청해주세요.</p>
            </div>

            <div className="rounded-[2.5rem] bg-[#0f0f12] border border-white/5 p-8 shadow-2xl space-y-8">
                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">휴대폰 번호</label>
                        <input
                            type="tel"
                            placeholder="010-0000-0000"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            className="w-full h-14 bg-white/5 border border-white/5 rounded-2xl px-6 text-sm font-bold text-white outline-none focus:border-indigo-500/50 transition-all"
                        />
                    </div>
                </div>

                <div className="p-6 rounded-3xl bg-indigo-500/5 border border-indigo-500/10 flex items-start gap-4">
                    <AlertCircle className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                        <p className="text-xs font-black text-indigo-500">안내 사항</p>
                        <p className="text-[10px] font-bold text-slate-500 leading-relaxed">
                            제출하신 번호는 관리자가 확인 후 승인해 드립니다.<br />
                            최대 24시간 내에 처리가 완료됩니다.
                        </p>
                    </div>
                </div>

                <button
                    onClick={handleSubmitRequest}
                    disabled={!phoneNumber || isSubmitting}
                    className="w-full h-16 rounded-2xl bg-indigo-600 text-white font-black shadow-xl shadow-indigo-600/20 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all uppercase tracking-widest text-sm flex items-center justify-center gap-3"
                >
                    {isSubmitting ? <RefreshCw className="h-5 w-5 animate-spin" /> : "승인 요청하기"}
                </button>
            </div>
        </div>
    );
};

export default PhoneVerificationPage;
